import { useState, useEffect, useMemo } from 'react';
import {
  Search as SearchIcon,
  Plus as AddIcon,
  Pencil as EditIcon,
  Trash2 as DeleteIcon,
  Wrench as BuildIcon,
  Code as CodeIcon,
} from 'lucide-react';
import { Button, Chip, FormField, Input, Textarea } from '@coordio/ui';
import { EmptyState, FormSection, InlineErrorPanel, LoadingSkeleton } from '@coordio/ui/layout';
import { CortexDialog, CortexDialogBody, CortexDialogFooter } from '../dialogs/CortexDialog';
import { agentService } from '../../services/agentService';
import { toolService } from '../../services/toolService';
import { useNotificationStore } from '../../store/notificationStore';
import { useConfirmDialog } from '../common/ConfirmDialog';
import type { ToolDto } from '../../types/tool';
import { CodeEditor } from '../common/CodeEditor';
import type {
  AgentDefinitionFunctionDto,
  CreateAgentDefinitionFunctionRequest,
  UpdateAgentDefinitionFunctionRequest,
} from '../../types/agent';

interface AgentDefinitionFunctionListProps {
  agentId: string;
  readOnly?: boolean;
  /** Initial list (e.g. from agent detail); if provided, we still refetch on mount for freshness. */
  initialFunctions?: AgentDefinitionFunctionDto[];
}

const DEFAULT_PARAMS_JSON = `{
  "type": "object",
  "properties": {},
  "required": []
}`;

/**
 * List and CRUD for agent-registered functions (OpenAI Assistants style).
 * Uses CortexDialog + FormField + design tokens for coordio-ui foundation.
 */
export const AgentDefinitionFunctionList = ({
  agentId,
  readOnly = false,
  initialFunctions,
}: AgentDefinitionFunctionListProps) => {
  const confirm = useConfirmDialog();
  const [functions, setFunctions] = useState<AgentDefinitionFunctionDto[]>(initialFunctions ?? []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingFn, setEditingFn] = useState<AgentDefinitionFunctionDto | null>(null);
  const [saving, setSaving] = useState(false);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formParametersJson, setFormParametersJson] = useState(DEFAULT_PARAMS_JSON);
  const [formSortOrder, setFormSortOrder] = useState(0);
  const [openCatalogAttachDialog, setOpenCatalogAttachDialog] = useState(false);
  const [catalogToolsPickList, setCatalogToolsPickList] = useState<ToolDto[]>([]);
  const [loadingCatalogTools, setLoadingCatalogTools] = useState(false);
  const [selectedCatalogToolIds, setSelectedCatalogToolIds] = useState<Set<string>>(new Set());
  const [catalogToolSearchTerm, setCatalogToolSearchTerm] = useState('');
  const [attachingCatalogTools, setAttachingCatalogTools] = useState(false);
  const { addNotification } = useNotificationStore();

  const filteredCatalogTools = useMemo(() => {
    if (!catalogToolSearchTerm.trim()) return catalogToolsPickList;
    const q = catalogToolSearchTerm.trim().toLowerCase();
    return catalogToolsPickList.filter(
      (t) =>
        (t.name ?? '').toLowerCase().includes(q) ||
        (t.displayName ?? '').toLowerCase().includes(q) ||
        (t.description ?? '').toLowerCase().includes(q)
    );
  }, [catalogToolsPickList, catalogToolSearchTerm]);

  const toggleCatalogToolSelection = (toolId: string) => {
    setSelectedCatalogToolIds((prev) => {
      const next = new Set(prev);
      if (next.has(toolId)) next.delete(toolId);
      else next.add(toolId);
      return next;
    });
  };

  const loadFunctions = async (signal?: AbortSignal) => {
    if (!agentId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await agentService.getFunctions(agentId, signal);
      if (signal?.aborted) return;
      setFunctions(data);
    } catch (err: any) {
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || signal?.aborted) {
        return;
      }
      const msg = err.response?.data?.message || 'Failed to load tools';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    loadFunctions(controller.signal);
    return () => controller.abort();
  }, [agentId]);

  const handleAdd = () => {
    setEditingFn(null);
    setFormName('');
    setFormDescription('');
    setFormParametersJson(DEFAULT_PARAMS_JSON);
    setFormSortOrder(functions.length);
    setOpenDialog(true);
  };

  const handleEdit = (fn: AgentDefinitionFunctionDto) => {
    setEditingFn(fn);
    setFormName(fn.name);
    setFormDescription(fn.description);
    setFormParametersJson(fn.parametersJson && fn.parametersJson !== '{}' ? fn.parametersJson : DEFAULT_PARAMS_JSON);
    setFormSortOrder(fn.sortOrder);
    setOpenDialog(true);
  };

  const handleDelete = async (fn: AgentDefinitionFunctionDto) => {
    const ok = await confirm({
      title: 'Delete tool',
      message: `Delete tool "${fn.name}"?`,
      confirmText: 'Delete',
    });
    if (!ok) return;
    try {
      await agentService.deleteFunction(agentId, fn.id);
      addNotification('Tool deleted', 'success');
      loadFunctions();
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to delete tool', 'error');
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingFn(null);
  };

  const handleOpenAttachCatalog = () => {
    setSelectedCatalogToolIds(new Set());
    setCatalogToolSearchTerm('');
    setOpenCatalogAttachDialog(true);
    setLoadingCatalogTools(true);
    setCatalogToolsPickList([]);
    toolService
      .getTools({ isActive: true, pageNumber: 1, pageSize: 200 })
      .then((page) => {
        const tools = page.items ?? [];
        const available = tools.filter(
          (t) =>
            !functions.some(
              (f) => f.name === t.name || (f.toolId != null && f.toolId === t.id)
            )
        );
        setCatalogToolsPickList(available);
      })
      .catch((err: any) => {
        addNotification(err.response?.data?.message || 'Failed to load catalog tools', 'error');
      })
      .finally(() => setLoadingCatalogTools(false));
  };

  const handleCatalogAttachDialogClose = () => {
    setOpenCatalogAttachDialog(false);
    setSelectedCatalogToolIds(new Set());
    setCatalogToolSearchTerm('');
  };

  const handleAttachCatalogTools = async () => {
    const ids = Array.from(selectedCatalogToolIds);
    if (ids.length === 0) {
      addNotification('Select at least one tool to attach', 'warning');
      return;
    }
    setAttachingCatalogTools(true);
    const failed: string[] = [];
    try {
      for (const toolId of ids) {
        try {
          await agentService.attachCatalogTool(agentId, toolId);
        } catch (err: any) {
          failed.push(toolId);
          addNotification(err.response?.data?.message || `Failed to attach tool ${toolId}`, 'error');
        }
      }
      const succeeded = ids.length - failed.length;
      if (succeeded > 0) {
        addNotification(
          succeeded === 1 ? 'Catalog tool attached' : `${succeeded} catalog tools attached`,
          'success'
        );
        handleCatalogAttachDialogClose();
        loadFunctions();
      }
    } finally {
      setAttachingCatalogTools(false);
    }
  };

  const handleDialogSave = async () => {
    const name = formName.trim();
    const description = formDescription.trim();
    if (!name) {
      addNotification('Name is required', 'warning');
      return;
    }
    if (!description) {
      addNotification('Description is required', 'warning');
      return;
    }
    const paramsJson = formParametersJson.trim() || '{}';
    try {
      JSON.parse(paramsJson);
    } catch {
      addNotification('Parameters must be valid JSON', 'warning');
      return;
    }
    setSaving(true);
    try {
      if (editingFn) {
        const payload: UpdateAgentDefinitionFunctionRequest = {
          name,
          description,
          parametersJson: paramsJson,
          sortOrder: formSortOrder,
        };
        await agentService.updateFunction(agentId, editingFn.id, payload);
        addNotification('Tool updated', 'success');
      } else {
        const payload: CreateAgentDefinitionFunctionRequest = {
          name,
          description,
          parametersJson: paramsJson,
          sortOrder: formSortOrder,
        };
        await agentService.createFunction(agentId, payload);
        addNotification('Tool added', 'success');
      }
      handleDialogClose();
      loadFunctions();
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to save tool', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <FormSection title="Registered Tools" data-testid="agent-section-tools">
        <LoadingSkeleton rows={3} />
      </FormSection>
    );
  }

  return (
    <FormSection
      title="Registered Tools"
      description="Functions the agent can call during conversations (OpenAI Assistants style)."
      data-testid="agent-section-tools"
    >
      {!readOnly && (
        <div className="flex justify-end gap-2 mb-3">
          <Button type="button" variant="outline" size="sm" onClick={handleAdd} data-testid="add-tool-btn">
            <AddIcon size={16} className="mr-1" />
            Add tool
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={handleOpenAttachCatalog} data-testid="attach-catalog-tool-btn">
            <BuildIcon size={16} className="mr-1" />
            Attach catalog tool
          </Button>
        </div>
      )}

      {error && (
        <div className="mb-3">
          <InlineErrorPanel
            message={error}
            action={
              <Button type="button" variant="ghost" size="sm" onClick={() => setError(null)}>
                Dismiss
              </Button>
            }
          />
        </div>
      )}

      {functions.length === 0 ? (
        <EmptyState
          title="No tools registered"
          description={
            readOnly
              ? 'This agent has no tools registered.'
              : 'Add tools (name, description, parameters schema) so the agent can call them during conversations.'
          }
        />
      ) : (
        <div className="border border-[var(--color-border-default)] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border-default)] bg-[var(--color-bg-subtle)]">
                <th className="px-3 py-2 text-left w-12 text-xs font-medium text-[var(--color-text-secondary)]" aria-label="Type" />
                <th className="px-3 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)]">Name</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)]">Description</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-[var(--color-text-secondary)]">Order</th>
                {!readOnly && (
                  <th className="px-3 py-2 text-right text-xs font-medium text-[var(--color-text-secondary)] w-[120px]">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {functions.map((fn) => (
                <tr key={fn.id} className="border-b border-[var(--color-border-subtle)] last:border-b-0 hover:bg-[var(--color-bg-subtle)]">
                  <td className="px-3 py-2 align-middle w-12 text-[var(--color-text-secondary)]">
                    {fn.isSystem ? (
                      <BuildIcon size={16} aria-label="System tool" />
                    ) : (
                      <CodeIcon size={16} aria-label="Custom tool" />
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-[var(--color-text-primary)]">{fn.name}</td>
                  <td className="px-3 py-2 max-w-[320px] text-[var(--color-text-secondary)]">{fn.description}</td>
                  <td className="px-3 py-2 text-right text-[var(--color-text-secondary)]">{fn.sortOrder}</td>
                  {!readOnly && (
                    <td className="px-3 py-2 text-right">
                      {!fn.isSystem && (
                        <Button type="button" variant="ghost" size="icon" onClick={() => handleEdit(fn)} aria-label="Edit tool">
                          <EditIcon size={16} />
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-[var(--color-danger)]"
                        onClick={() => handleDelete(fn)}
                        aria-label="Delete tool"
                      >
                        <DeleteIcon size={16} />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ─── Add / Edit tool dialog ────────────────────────────────────── */}
      <CortexDialog
        open={openDialog}
        onClose={handleDialogClose}
        title={editingFn ? 'Edit tool' : 'Add tool'}
        size="lg"
        data-testid="tool-function-dialog"
      >
        <CortexDialogBody>
          <div className="flex flex-col gap-3">
            <FormField label="Name" description="Unique name the LLM will use to call this function" required>
              <Input
                autoFocus
                placeholder="e.g. get_weather"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
              />
            </FormField>

            <FormField label="Description" required>
              <Textarea
                placeholder="What this function does (shown to the LLM)"
                rows={2}
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
              />
            </FormField>

            <FormField label="Sort order">
              <Input
                type="number"
                value={formSortOrder}
                onChange={(e) => setFormSortOrder(parseInt(e.target.value, 10) || 0)}
                min={0}
              />
            </FormField>

            <FormField label="Parameters (JSON schema)">
              <CodeEditor
                value={formParametersJson}
                onChange={setFormParametersJson}
                language="json"
                height="240px"
                readOnly={false}
              />
            </FormField>
          </div>
        </CortexDialogBody>
        <CortexDialogFooter>
          <Button type="button" variant="outline" onClick={handleDialogClose}>
            Cancel
          </Button>
          <Button type="button" variant="default" onClick={handleDialogSave} disabled={saving}>
            {saving ? 'Saving…' : editingFn ? 'Update' : 'Add'}
          </Button>
        </CortexDialogFooter>
      </CortexDialog>

      {/* ─── Attach catalog tool dialog ────────────────────────────────── */}
      <CortexDialog
        open={openCatalogAttachDialog}
        onClose={handleCatalogAttachDialogClose}
        title="Attach catalog tool"
        size="md"
        data-testid="attach-catalog-tool-dialog"
      >
        <CortexDialogBody>
          {loadingCatalogTools ? (
            <div className="flex justify-center py-6">
              <LoadingSkeleton rows={3} />
            </div>
          ) : catalogToolsPickList.length === 0 ? (
            <EmptyState
              title="No tools available"
              description="No active catalog tools available to attach, or all are already attached to this agent."
            />
          ) : (
            <div className="flex flex-col gap-3">
              <div className="relative">
                <SearchIcon size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-text-secondary)]" />
                <Input
                  className="pl-8"
                  placeholder="Search tools..."
                  value={catalogToolSearchTerm}
                  onChange={(e) => setCatalogToolSearchTerm(e.target.value)}
                  aria-label="Search catalog tools"
                />
              </div>

              {filteredCatalogTools.length === 0 ? (
                <p className="text-sm text-[var(--color-text-secondary)] text-center py-4">
                  No tools match your search.
                </p>
              ) : (
                <div className="flex flex-col border border-[var(--color-border-default)] rounded-lg overflow-hidden max-h-[320px] overflow-y-auto">
                  {filteredCatalogTools.map((tool) => (
                    <button
                      key={tool.id}
                      type="button"
                      className={`w-full text-left px-3 py-2.5 cursor-pointer border-b border-[var(--color-border-subtle)] last:border-b-0 transition-colors ${
                        selectedCatalogToolIds.has(tool.id)
                          ? 'bg-[var(--color-accent-subtle)]'
                          : 'hover:bg-[var(--color-bg-subtle)]'
                      }`}
                      onClick={() => toggleCatalogToolSelection(tool.id)}
                    >
                      <span className="block font-mono text-sm text-[var(--color-text-primary)]">
                        {tool.displayName || tool.name}
                      </span>
                      <span className="block text-xs text-[var(--color-text-secondary)] mt-0.5">
                        {tool.description}
                      </span>
                      <span className="flex gap-1 flex-wrap mt-1.5">
                        {tool.isDefault && <Chip label="Default" color="accent" variant="outlined" />}
                        {tool.isSystem && <Chip label="System" color="violet" variant="outlined" />}
                        {!tool.isDefault && !tool.isSystem && <Chip label="Custom" variant="outlined" />}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </CortexDialogBody>
        <CortexDialogFooter>
          <Button type="button" variant="outline" onClick={handleCatalogAttachDialogClose}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="default"
            onClick={handleAttachCatalogTools}
            disabled={
              selectedCatalogToolIds.size === 0 ||
              attachingCatalogTools ||
              catalogToolsPickList.length === 0
            }
          >
            {attachingCatalogTools
              ? 'Attaching…'
              : selectedCatalogToolIds.size > 0
                ? `Attach (${selectedCatalogToolIds.size})`
                : 'Attach'}
          </Button>
        </CortexDialogFooter>
      </CortexDialog>
    </FormSection>
  );
};
